import { frameId } from "../assets/manifest.ts";
import { applyCommand, type Command } from "../document/commands.ts";
import { EMPTY_HISTORY, canRedo, canUndo, execute, redo, undo, type EditorState, type History } from "../document/history.ts";
import { EMPTY_TILE } from "../document/packed-tile.ts";
import { atSavePoint, droppedByExecute, savePointOf, type SavePoint } from "../document/save-point.ts";
import type { CompileContext } from "../compile/context.ts";
import type { PropFootprint } from "../compile/footprint.ts";
import type { FrameDepth } from "../compile/ground-depth.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import type { CellRect } from "../terrain/match.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import { COLLISION_CELLS_PER_TILE, type JsonValue, type LayerDocument, type MapDocument, type WorldDocument } from "../document/types.ts";
import { LayerHighlightState } from "./layer-highlight-state.ts";
import { layersContentEditedBy, layersEditedBy } from "./layer-lock.ts";
import { ManyPropEdits } from "./many-prop-edits.ts";
import { CellStackHover } from "./cell-mark.ts";
import { cellStack, type CellStackEntry } from "./cell-stack.ts";
import { FootprintDrafts } from "./footprint-drafts.ts";
import { PresetEdits } from "./preset-edits.ts";
import { setMapPropertiesCommand } from "./panel-commands.ts";
import { mapGroupLabelOf, offeredEntityTypes, registerAdapter, registeredEntityTypes, type CameraDefinition, type CollisionBrush, type DaedalusGameAdapter, type StandInDefinition, type ZoneTypeDefinition } from "./adapter.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { CollisionAuthoring } from "./collision-authoring.ts";
import { DoorAuthoring } from "./door-authoring.ts";
import { RoomEdits } from "./room-edits.ts";
import { ZoneAuthoring } from "./zone-authoring.ts";
import { contextFrameSize, thingBoxOn } from "./cell-sources.ts";
import { Lookups } from "./lookups.ts";
import { MapEdits, neighbourOf } from "./map-edits.ts";
import { commandMap } from "../document/command-map.ts";
import { EMPTY_CATALOGUE, tilesetHeading, type CataloguePackInfo, type EditorCatalogue } from "./catalogue.ts";
import { LibrarySource, type ContextCompletion } from "./library-source.ts";
import { ActivationOnMap } from "./activation-on-map.ts";
import { DensityMeter } from "./density-meter.ts";
import { CellFieldEdits } from "./cell-field-edits.ts";
import { PathFieldEdits } from "./path-field-edits.ts";
import type { EntityTypeDefinition, JsonSchema } from "./schema.ts";
import { SessionManager } from "./sessions.ts";
import type { IssueEntry } from "./issue-entries.ts";
import { doorAt } from "../document/doors.ts";
import { addPackCommand, dropFramesCommand, offeredTilesetIds, pullFramesCommand, pullPicksCommand, removePackCommand } from "./packs.ts";
import type { IssueFix } from "./prop-align.ts";
import { presenceInsideOf } from "./presence.ts";
import { GroundPick } from "./ground-pick.ts";
import { groundUnderPixel } from "./ground-at.ts";
import { StatePreviews } from "./state-preview.ts";
import { StandInFeet } from "./stand-in.ts";
import { StandInPresence } from "./stand-in-presence.ts";
import { ReplaceEdits } from "./replace-edits.ts";
import { suggestedAnimations } from "./suggest-animations.ts";
import { SelectionMenu } from "./selection-menu.ts";
import { ReferenceEdits } from "./reference-edits.ts";
import { AreaEdits, type AreaSlice } from "./area-edits.ts";
import { liveRepaintOf, type LiveRepaint } from "../live/repaint.ts";
import { LayoutApply } from "./layout-apply.ts";
import { LiveEdits } from "./live-edits.ts";
import { liveRedrawOf } from "./live-repaint.ts";
import { LayerAuthoring, type LayerSlice } from "./layer-authoring.ts";
import type { MapView } from "./layer-map-view.ts";
import { PropAlignment, type PropAlignmentSlice } from "./prop-alignment.ts";
import { ViewState, type EditorTool, type ViewSlice } from "./view-state.ts";
import { IssueChecks, type IssueSlice } from "./issue-checks.ts";
import { FillAuthoring, type FillSlice } from "./fill-authoring.ts";
import { TerrainAuthoring, type TerrainSlice } from "./terrain-authoring.ts";
import { TileAuthoring, type TileSlice } from "./tile-authoring.ts";
import { PropAuthoring, type PropSlice } from "./prop-authoring.ts";
import { PropSelection, type PropSelectionSlice } from "./prop-selection.ts";
import { PropBrush, type PropBrushSlice } from "./prop-brush.ts";

export type { SnapMode } from "./prop-authoring.ts";
export type { EditorTool } from "./view-state.ts";
export type { TerrainChoice } from "./terrain-authoring.ts";

/**
 * The editor store (ADR-021): the document with its history, one incremental session per terrain layer,
 * the stroke in progress, view state (active, hidden and locked layers, brush size), and change channels.
 * Panels subscribe to state changes; the renderer subscribes to repaints. Pure of Phaser and React.
 */

/**
 * Cells to repaint on one layer.
 */
export interface Repaint {
  readonly layerId: string;
  readonly rect: CellRect;
}

/**
 * Listener for repaints; `null` means every layer must be repainted (structure changed). Prop, entity and
 * rename commands change no cells and emit nothing; consumers read those through `subscribe`.
 */
export type RepaintListener = (repaint: Repaint | null) => void;

/**
 * The bounding rectangle of cell indices in a row-major grid.
 *
 * @param indices Cell indices.
 * @param width Grid width.
 * @returns The rectangle, empty when there are no indices.
 */
function cellBounds(indices: ArrayLike<number>, width: number): CellRect {
  const rect: CellRect = { x0: Number.MAX_SAFE_INTEGER, y0: Number.MAX_SAFE_INTEGER, x1: 0, y1: 0 };
  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i] as number;
    const x = index % width;
    const y = (index - x) / width;
    rect.x0 = Math.min(rect.x0, x);
    rect.y0 = Math.min(rect.y0, y);
    rect.x1 = Math.max(rect.x1, x + 1);
    rect.y1 = Math.max(rect.y1, y + 1);
  }
  return indices.length === 0 ? { x0: 0, y0: 0, x1: 0, y1: 0 } : rect;
}

/**
 * The tiles a rectangle of collision cells lies in.
 *
 * @param rect Collision cells.
 * @returns Tiles.
 */
function tileBoundsOfCollision(rect: CellRect): CellRect {
  return { x0: Math.floor(rect.x0 / COLLISION_CELLS_PER_TILE), y0: Math.floor(rect.y0 / COLLISION_CELLS_PER_TILE), x1: Math.ceil(rect.x1 / COLLISION_CELLS_PER_TILE), y1: Math.ceil(rect.y1 / COLLISION_CELLS_PER_TILE) };
}


/**
 * The zone types of a game that registers none, one list so the Zones overlay's memo holds.
 */
const NO_ZONE_TYPES: readonly ZoneTypeDefinition[] = [];

/**
 * A save point no history reaches, for a tab whose history was emptied while it held unsaved work: its top is
 * a command of its own, never recorded, so the state reads unsaved until the next save.
 */
const UNSAVED_POINT: SavePoint = { top: { kind: "compound", commands: [] }, dropped: 0 };

/**
 * The packs a catalogue without any lists, one array, so a reader that compares them by identity sees no change.
 */
const NO_PACKS: readonly CataloguePackInfo[] = [];

/**
 * The store over a world's document with one map in view.
 */
export class EditorStore {
  private state: EditorState;
  private openMap: string;
  private readonly neighbours = new Map<string, { map: MapDocument; tilesets: WorldDocument["tilesets"]; sessions: SessionManager; failed: boolean; idle: Set<string> }>();
  private readonly neighbourTiles = new WeakMap<object, Uint32Array>();
  private readonly mapViews = new Map<string, MapView>();
  private readonly sessions: SessionManager;
  private readonly adapter: DaedalusGameAdapter;
  private readonly listeners = new Set<() => void>();
  private readonly repaintListeners = new Set<RepaintListener>();
  private nextId = 1;
  private readonly terrainAuthoring: TerrainAuthoring;
  private readonly tileAuthoring: TileAuthoring;
  private readonly fillAuthoring: FillAuthoring;
  private readonly layerAuthoring: LayerAuthoring;
  private readonly propAuthoring: PropAuthoring;
  private captured: Command[] | null = null;
  private trialBase: WorldDocument | null = null;
  private readonly alignment: PropAlignment;
  private readonly selectionState: PropSelection;
  private readonly viewState: ViewState;
  private dropped = 0;
  private saved: SavePoint = { top: null, dropped: 0 };
  private idTag: string | null = null;
  private readonly checks: IssueChecks;

  /**
   * Props against the collision cells (ADR-040 amendment of 2026-10-03, "props stand on the cells"): where a prop
   * goes to stand on them, the props and groups off them, Align props to collision cells and its one-prop fix,
   * Keep as placed, and the fix an Issues line offers.
   */
  public readonly propAlignment: PropAlignmentSlice;

  /**
   * Fill (P1.6-T40): the region under a point on the active layer filled as one command, and the fill over
   * `FILL_ASK_TILES` that waits to be confirmed or cancelled.
   */
  public readonly fill: FillSlice;

  /**
   * Zone authoring (ADR-054's amendment of 2026-09-27, decisions 4 and 5): the type in hand, painting and erasing a
   * zone layer's cells, the cells a zone stroke covers, a prop's zone stamp (ADR-096 decision 38), the Zones and
   * Activation overlays' toggles and the Zones overlay.
   */
  public readonly zones: ZoneAuthoring;

  /**
   * Door authoring (ADR-122 decisions 2.1, 2.4 and 9): the door in hand, painting and erasing a door layer, a new
   * door and its destination and fields, and the way back.
   */
  public readonly doors: DoorAuthoring;

  /**
   * The checks over the map in view (ADR-053, ADR-070's amendments): Daedalus's own checks and the game's
   * validation, the lowered map they read, the Issues list's lines and the map's ignores, the Behaviours view, the
   * names and boxes they name things by, and the revision of what they read besides the document.
   */
  public readonly issues: IssueSlice;

  /**
   * Collision painting (ADR-028, ADR-040): the brush, its shape and the ellipse's extents, painting a collision
   * layer's cells, the map's effective grid as the compiler would build it, and what makes a cell block.
   */
  public readonly collision: CollisionAuthoring;

  /**
   * The editor's view toggles and picks (ADR-045, ADR-071, P1.6-T47, ADR-113 decision 14): the pointer tool, the
   * player wash, the stand-in and its selection, the camera-stop guide, the footprint panel's feet box and the ground
   * cell the select tool last picked. View state outside history.
   */
  public readonly view: ViewSlice;

  /**
   * Terrain authoring (ADR-038, ADR-072): the brush size, the stroke on the active layer, each terrain layer's
   * colour, the names of terrain entries and of the layers that follow them, choosing a terrain and colour from the
   * palette, a layer's terrain and its Cliff height.
   */
  public readonly terrain: TerrainSlice;

  /**
   * Prop and marker authoring (ADR-027, ADR-040, ADR-053): how placement snaps, the footprints the compile context
   * holds for frames, placing, mirroring, offsetting, moving and removing props and markers, a prop's own footprint
   * and its behaviour.
   */
  public readonly props: PropSlice;

  /**
   * The prop and marker selections (ADR-066 decision 5, P1.6-T33) and the templates saved from and placed by them
   * (ADR-066 decisions 6 to 8): the gathered props, the selected markers, the template in hand, capturing and placing a
   * template.
   */
  public readonly selection: PropSelectionSlice;

  /**
   * The prop brush (ADR-123 decisions 4 to 7): the brush set in hand, its radius and density, the stroke counter,
   * the ghosts under the pointer, the stroke written as one command and the erase as one removal.
   */
  public readonly brush: PropBrushSlice;

  /**
   * Tile authoring (ADR-033, ADR-044, ADR-089): the tile in hand and its mirrors, painting tile cells, the stroke that
   * draws as it paints, and the tile selection with its clipboard.
   */
  public readonly tiles: TileSlice;

  /**
   * Layer authoring (ADR-025, ADR-043, ADR-047): the active layer, the layers hidden and locked by their own eye and
   * lock and by their groups', the groups, the Players band and its lock, and moving, adding, renaming and removing
   * layers.
   */
  public readonly layers: LayerSlice;

  /**
   * Where the stand-in's feet stand (ADR-071 decision 6): view state in a holder with listeners of its own,
   * so moving the stand-in never notifies the store's subscribers. Empty while the stand-in is not shown.
   */
  public readonly standInFeet = new StandInFeet();

  /**
   * The line of the inspector's "In this cell" list the pointer or the focus is on: view state in
   * a holder with listeners of its own, so pointing at a line never notifies the store's subscribers.
   */
  public readonly cellStackHover = new CellStackHover();

  /**
   * The states placed props are previewed in on the map (ADR-058 amendment, P1.3-T4): view state that is
   * never saved or undone, whose changes reach the store's subscribers, including every presence prop shown in its
   * inside state by the registered types (P1.6-T41).
   */
  public readonly statePreviews = new StatePreviews(
    () => this.notify(),
    (prop) => presenceInsideOf(this.registeredObjectTypes, prop),
    (layerId, prop) => this.standInPresence.answer(layerId, prop.id),
  );

  /**
   * The Activation overlay's toggle and the prop Edit on map paints (P1.6-T32): view state whose changes reach
   * the store's subscribers, and whose editing is let go whenever its prop can no longer be edited.
   */
  public readonly activation = new ActivationOnMap({
    propOf: (layerId, propId) => {
      const layer = this.map.layers.find((l) => l.id === layerId);
      return layer?.kind === "prop" ? layer.props.find((p) => p.id === propId) : undefined;
    },
    isEditable: (layerId) => this.layers.isEditable(layerId),
    notify: () => this.notify(),
    map: () => this.map,
    context: () => this.context,
    tileSize: () => this.document.tileSize,
    shownState: (layerId, prop) => this.statePreviews.shownStateOf(layerId, prop),
    issues: () => this.issues.validate(),
    shown: (layerId) => !this.layers.isHidden(layerId),
    types: () => this.registeredObjectTypes,
    onStart: () => {
      this.cellFields.endEditing();
      this.pathFields.endEditing();
    },
  });

  /**
   * Edit on map for a cells field (P1.6-T36): view state naming the field the map paints, each stroke one
   * command, ending activation's Edit on map when it starts and ended by it.
   */
  public readonly cellFields: CellFieldEdits = new CellFieldEdits({
    map: () => this.map,
    types: () => ({ objectTypes: this.registeredObjectTypes, entityTypes: this.registeredEntityTypes }),
    lowered: () => this.issues.loweredCells(),
    box: (owner) => thingBoxOn(this.map, owner, this.document.tileSize, contextFrameSize(this.context)),
    run: (command) => this.run(command),
    setManyField: (name, value) => this.many.setField(name, value),
    propSelection: () => this.selectionState.props,
    markerSelection: () => this.selectionState.markerRefs,
    isEditable: (layerId) => this.layers.isEditable(layerId),
    notify: () => this.notify(),
    endActivation: () => {
      this.activation.endEditing();
      this.pathFields.endEditing();
    },
  });

  /**
   * Edit on map for a path field (ADR-136): view state naming the path the map edits, each edit one command,
   * ending activation's and the cells fields' Edit on map when it starts and ended by them.
   */
  public readonly pathFields: PathFieldEdits = new PathFieldEdits({
    map: () => this.map,
    types: () => ({ objectTypes: this.registeredObjectTypes, entityTypes: this.registeredEntityTypes }),
    box: (owner) => thingBoxOn(this.map, owner, this.document.tileSize, contextFrameSize(this.context)),
    run: (command) => this.run(command),
    setManyField: (name, value) => this.many.setField(name, value),
    propSelection: () => this.selectionState.props,
    markerSelection: () => this.selectionState.markerRefs,
    isEditable: (layerId) => this.layers.isEditable(layerId),
    notify: () => this.notify(),
    endOthers: () => {
      this.activation.endEditing();
      this.cellFields.endEditing();
    },
  });

  /**
   * The lists lookup fields choose from (ADR-021's amendment of 2026-10-02), handed over by the host: view state
   * whose changes reach the store's subscribers.
   */
  public readonly lookups = new Lookups(() => this.notify());

  /**
   * The prop selection's edits (P1.6-T33): Delete, a move, a mirror and a sort offset, a type switched on and
   * a field set for every selected prop, Copy and Paste behaviour, and Select every prop of this frame and of
   * this type, each one command over the selection.
   */
  public readonly many = new ManyPropEdits({
    map: () => this.map,
    run: (command) => this.run(command),
    selection: () => this.selectionState.props,
    select: (refs) => this.selection.setProps(refs),
    editable: (layerId) => this.layers.isEditable(layerId),
    locked: (layerId) => this.layers.isLocked(layerId),
    hidden: (layerId) => this.layers.isHidden(layerId),
    tileSize: () => this.document.tileSize,
    quantise: (value) => this.propAuthoring.quantise(value),
    stepped: (prop, stepX, stepY) => this.alignment.steppedAt(prop, stepX, stepY),
    types: () => this.registeredObjectTypes,
    switchedOn: (definition, prop) => this.propAuthoring.switchedOn(definition, prop),
    notify: () => this.notify(),
    guardDelete: (refs, run) => this.references.guardDelete(refs.map((ref) => ({ kind: "prop", layerId: ref.layerId, propId: ref.propId })), run),
  });

  /**
   * Replace in selection (ADR-097): the dialog's state, what it lists and Apply, one command over the tile
   * selection's rectangle or, once asked for, the whole active layer; terrain becomes any entry the terrain
   * palette offers, on its own layer or a new one, placed where the author says; collision values become a
   * brush's value and zone types a registered type (P1.6-T117); props by frame or by type take the prop in
   * hand (P1.6-T115).
   */
  public readonly replace = new ReplaceEdits({
    world: () => this.document,
    map: () => this.map,
    activeLayerId: () => this.layers.activeId,
    selection: () => this.tiles.selection,
    isLocked: (layerId) => this.layers.isLocked(layerId),
    isHidden: (layerId) => this.layers.isHidden(layerId),
    layerColour: (layerId) => this.terrain.layerColour(layerId),
    tileInHand: () => this.tiles.brush,
    terrains: () => this.terrains.map((terrain) => this.sessions.usableTerrain(terrain)),
    terrainName: (terrainId, colour) => this.terrain.name(terrainId, colour),
    collisionBrushes: () => this.collisionBrushes,
    zoneTypes: () => this.zoneTypes,
    frameSize: (tileset, tile) => ({ ...(frameSizeOf(this.context, frameId(tileset, null, tile)) ?? { w: this.document.tileSize, h: this.document.tileSize }), base: 0 }),
    shownFrame: (layerId, prop) => this.statePreviews.frameOf(layerId, prop),
    suggestedStateFrame: (type, stateId, placed) => this.props.suggestedStateFrame(type, stateId, placed),
    suggestedAnimations: (type, placed) => {
      const definition = this.registeredObjectTypes.find((t) => t.type === type);
      return definition === undefined ? {} : suggestedAnimations(definition, placed, this.catalogue, this.adapter.suggestAnimation);
    },
    newLayerId: () => this.allocateId("layer"),
    run: (command) => this.run(command),
    select: (refs) => this.selection.setProps(refs),
    resolvedTiles: (layerId) => this.sessions.get(layerId)?.output ?? null,
    setActiveLayer: (layerId) => this.layers.setActive(layerId),
    notify: () => this.notify(),
  });

  /**
   * The menu a right press inside the tile selection opens (ADR-097 decision 14), view state.
   */
  public readonly selectionMenu = new SelectionMenu({ selection: () => this.tiles.selection, notify: () => this.notify() });

  /**
   * Typed references (P1.6-T35): Pick on map, Clear, the Links overlay and the question a delete that would
   * break a reference waits for, all view state; each field set is one command.
   */
  public readonly references: ReferenceEdits = new ReferenceEdits({
    document: () => this.document,
    map: () => this.map,
    arrivalType: () => this.adapter.arrivalType,
    types: () => ({ objectTypes: this.registeredObjectTypes, entityTypes: this.registeredEntityTypes }),
    run: (command) => this.run(command),
    setManyField: (name, value) => this.many.setField(name, value),
    propSelection: () => this.selectionState.props,
    markerSelection: () => this.selectionState.markerRefs,
    isLocked: (layerId) => this.layers.isLocked(layerId),
    isHidden: (layerId) => this.layers.isHidden(layerId),
    notify: () => this.notify(),
  });

  /**
   * The area (ADR-128 sections A and B, decisions 33 and 41c): the select tool's reach, the rectangle an `all`
   * selection holds and the one a drag is drawing, what they hold, and Delete and the box erase, each one compound.
   * View state outside history.
   */
  private readonly areaEdits = new AreaEdits({
    document: () => this.document,
    map: () => this.map,
    mapId: () => this.mapId,
    activeLayerId: () => this.layers.activeId,
    isLocked: (layerId) => this.layers.isLocked(layerId),
    isHidden: (layerId) => this.layers.isHidden(layerId),
    guard: () => ({ types: { objectTypes: this.registeredObjectTypes, entityTypes: this.registeredEntityTypes }, ...(this.adapter.arrivalType === undefined ? {} : { arrivalType: this.adapter.arrivalType }) }),
    allocateIds: (prefix, count) => this.allocateIds(prefix, count),
    run: (command) => this.run(command),
    notify: () => this.notify(),
  });

  /**
   * The area as callers reach it (`AreaSlice`).
   */
  public readonly area: AreaSlice = this.areaEdits;

  /**
   * Behaviour presets over the prop selection (P1.6-T34): Save from the lone selected prop, and Apply to the
   * selected props whose frame fits, sized as the compile sizes frames, one command.
   */
  public readonly presets = new PresetEdits({
    map: () => this.map,
    run: (command) => this.run(command),
    selection: () => this.selectionState.props,
    types: () => this.registeredObjectTypes,
    tilesets: () => this.document.tilesets,
    sizeOf: (frame) => frameSizeOf(this.context, frameId(frame.tileset, null, frame.tile)),
  });

  /**
   * Highlight layer (ADR-070's amendment of 2026-10-04): whether the active layer's content is highlighted on
   * the map, and that highlight. View state outside history.
   */
  public readonly layerHighlight = new LayerHighlightState({
    map: () => this.map,
    activeLayerId: () => this.layers.activeId,
    sources: () => ({ cells: (layerId) => this.layerCells(layerId), frameSize: (frame) => frameSizeOf(this.context, frameId(frame.tileset, null, frame.tile)), tileSize: this.document.tileSize, tilesets: this.document.tilesets.map((t) => t.id), playersAbove: this.layers.playersAbove }),
    notify: () => this.notify(),
  });

  /**
   * The stand-in's answers for the map's presence props (ADR-071 decision 5), which the state previews
   * draw while it is shown. View state; it drives nothing until the host binds its frames (`bindFrames`),
   * whose base and art bounds only the host's asset manifest holds.
   */
  public readonly standInPresence = new StandInPresence({
    map: () => this.map,
    feet: () => (this.viewState.standInShown ? this.standInFeet.point : null),
    standIn: () => this.standIn,
    tileSize: () => this.document.tileSize,
    frameSize: (id) => frameSizeOf(this.context, id),
    insideOf: (prop) => presenceInsideOf(this.registeredObjectTypes, prop),
  });

  /**
   * The footprint edits panels hold unwritten and the footprints written since the world's last save
   * (P1.6-T89): view state, whose changes reach the store's subscribers.
   */
  public readonly footprintDrafts = new FootprintDrafts(() => this.notify());

  /**
   * The ground eyedropper's view state (ADR-123's amendment of 2026-10-13, A5.4, P1.6-T249).
   */
  public readonly groundPick = new GroundPick({
    groundUnder: (x, y) => groundUnderPixel(this.map, this.document.tilesets, this.document.tileSize, x, y),
    notify: () => this.notify(),
  });

  /**
   * The density meter (ADR-088): view state reading the open map's lowered cells, its tile layers' resolved
   * cells (a terrain layer's session output, a tile layer's tiles) and the game's registration.
   */
  public readonly density = new DensityMeter({
    lowered: () => this.issues.loweredCells().map,
    tileSize: () => this.document.tileSize,
    chunkSize: () => this.document.chunkSize,
    cells: (layerId) => {
      const session = this.sessions.get(layerId);
      if (session !== undefined) {
        return session.output;
      }
      const layer = this.map.layers.find((l) => l.id === layerId);
      return layer?.kind === "tile" ? layer.tiles : null;
    },
    entityTypes: () => this.registeredEntityTypes,
    definition: () => this.adapter.density ?? null,
    view: () => this.adapter.camera?.view ?? null,
    notify: () => this.notify(),
  });

  /**
   * The world's maps (P1.6-T38): listed, opened, created, renamed and removed, each change one command.
   */
  public readonly maps = new MapEdits({
    document: () => this.document,
    mapId: () => this.mapId,
    open: (mapId) => this.openMapView(mapId),
    forget: (mapId) => void this.mapViews.delete(mapId),
    run: (command) => this.run(command),
    startMap: () => this.adapter.startMap,
    groupLabel: () => mapGroupLabelOf(this.adapter),
    viewCentre: () => this.viewState.viewCentre,
    focus: (tile) => (tile === null ? this.viewState.dropFocus() : this.viewState.focusAt(tile.x, tile.y)),
    idTag: () => this.idTag,
  });

  /**
   * Rooms (ADR-122 decisions 1.2 to 1.4, 3.1 and 9): the maps and arrival points a host is picked from, a new room
   * as one command, and the host, sharing and atmosphere of a map that exists.
   */
  public readonly rooms = new RoomEdits({
    document: () => this.document,
    open: (mapId) => this.openMapView(mapId),
    forget: (mapId) => void this.mapViews.delete(mapId),
    run: (command) => this.run(command),
    allocateId: (prefix) => this.allocateId(prefix),
    idTag: () => this.idTag,
    arrivalType: () => this.adapter.arrivalType,
    roomZone: () => this.zoneTypes.find((type) => type.id === this.adapter.roomZone) ?? null,
    atmosphereLists: () => ({ music: this.adapter.atmosphere?.music ?? [], ambience: this.adapter.atmosphere?.ambience ?? [] }),
  });

  /**
   * The world layout's preview and apply (ADR-109 decision 15, P1.6-T142): a plan's commands as one compound
   * command, one history entry, one undo.
   */
  public readonly worldLayout = new LayoutApply({
    document: () => this.document,
    forget: (mapId) => void this.mapViews.delete(mapId),
    run: (command) => this.run(command),
    idTag: () => this.idTag,
  });

  /**
   * The store's session (ADR-086 decisions 26 and 27, P1.11-T28), none until a host attaches one. With one,
   * the document is the session's shown document: every command travels as operations before it shows, undo
   * and redo are the tab's own, and what the others do is adopted with only what changed drawn again.
   */
  public readonly live: LiveEdits = new LiveEdits({
    held: () => this.terrainAuthoring.painting || this.tileAuthoring.strokes.active || this.brush.stroking,
    adopt: (document, repaint) => this.adoptLive(document, repaint),
    opened: (document) => this.terrainAuthoring.tidied(document),
    tag: (tag) => this.setIdTag(tag),
    notify: () => this.notify(),
  });

  /**
   * @param document The world document.
   * @param context Compile context (terrains, rule sets).
   * @param mapId Map to open first.
   * @param adapter The game's adapter (ADR-024).
   * @param catalogue Tileset and prop frame names for the palettes (ADR-026); empty when the world has none.
   * @param complete Derives the context's collision rules and surfaces again when the library grows terrains or
   *   tilesets (ADR-130 decision 2.8); none leaves the context as it grows.
   * @throws Error when the map is missing, a terrain or rule set is unknown, or the adapter registers an
   *   invalid or duplicate entity type.
   */
  public constructor(document: WorldDocument, context: CompileContext, mapId: string, adapter: DaedalusGameAdapter, catalogue: EditorCatalogue = EMPTY_CATALOGUE, complete?: ContextCompletion) {
    const library = new LibrarySource(catalogue, context, complete);
    this.library = library;
    this.openMap = mapId;
    this.terrainAuthoring = new TerrainAuthoring({
      document: () => this.document,
      map: () => this.map,
      mapId: () => this.mapId,
      terrains: () => library.context.terrains,
      packs: () => library.catalogue.packs ?? NO_PACKS,
      activeLayerId: () => this.layers.activeId,
      activeRaw: () => this.layerAuthoring.activeRaw,
      session: (layerId) => this.sessions.get(layerId),
      faceOf: (terrainId) => this.sessions.faceOf(terrainId),
      usableTerrain: (terrain) => this.sessions.usableTerrain(terrain),
      sample: (terrainId, colour, patch) => this.sessions.sample(this.state.document, this.map.seed, terrainId, colour, patch),
      isLocked: (layerId) => this.layers.isLocked(layerId),
      isEditable: (layerId) => this.layers.isEditable(layerId),
      refusesEdits: (layerId) => !this.layers.isEditable(layerId) || this.live.refusal() !== null,
      liveRefuses: () => this.liveRefuses(),
      setActiveLayer: (layerId) => this.layers.setActive(layerId),
      groupOf: (layerId) => this.layers.groups.of(layerId),
      groupNamed: (name) => this.layerAuthoring.groups.byName(name),
      groupOpen: (groupId) => !this.layers.groups.isLocked(groupId) && !this.layers.groups.isHidden(groupId),
      insertLayer: (kind, name, terrainId, group, colour) => this.layerAuthoring.insert(kind, name, terrainId, group, colour),
      run: (command) => this.run(command),
      provisional: (make) => this.provisional(make),
      addPack: (packId, tilesets) => this.addPack(packId, tilesets),
      notify: () => this.notify(),
      emitRepaint: (repaint) => this.emitRepaint(repaint),
    });
    this.terrain = this.terrainAuthoring;
    this.state = { document: this.terrainAuthoring.tidied(document), history: EMPTY_HISTORY };
    this.adapter = registerAdapter(adapter);
    this.viewState = new ViewState({
      camera: () => this.camera,
      standIn: () => this.standIn,
      mapPixels: () => this.mapPixels(),
      placeFeet: (point, width, height) => this.standInFeet.place(point, width, height),
      clearFeet: () => this.standInFeet.clear(),
      dropSelections: () => {
        this.tileAuthoring.editing.drop();
        this.selectionState.clear();
      },
      notify: () => this.notify(),
    });
    this.view = this.viewState;
    this.checks = new IssueChecks({
      document: () => this.document,
      map: () => this.map,
      get context() {
        return library.context;
      },
      adapter: this.adapter,
      get catalogue() {
        return library.catalogue;
      },
      objectTypes: () => this.registeredObjectTypes,
      entityTypes: () => this.registeredEntityTypes,
      faceOf: (terrainId) => this.sessions.faceOf(terrainId),
      malformedFaces: () => this.sessions.malformedFaces,
      links: () => this.references.links(),
      lookup: (name) => this.lookups.get(name),
      collision: () => this.collision.grid(),
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.issues = this.checks;
    this.propAuthoring = new PropAuthoring({
      document: () => this.document,
      map: () => this.map,
      get context() {
        return library.context;
      },
      get catalogue() {
        return library.catalogue;
      },
      adapter: this.adapter,
      objectTypes: () => this.registeredObjectTypes,
      entityTypes: () => this.registeredEntityTypes,
      requireEditable: (layerId) => this.requireEditable(layerId),
      allocateId: (prefix) => this.allocateId(prefix),
      stepped: (prop, stepX, stepY) => this.alignment.steppedAt(prop, stepX, stepY),
      select: (refs) => this.selection.setProps(refs),
      guardDelete: (things, run) => this.references.guardDelete(things, run),
      contextChanged: () => this.checks.bump(),
      repaintAll: () => this.emitRepaint(null),
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.props = this.propAuthoring;
    this.alignment = new PropAlignment({
      map: () => this.map,
      tileSize: () => this.document.tileSize,
      get context() {
        return library.context;
      },
      selection: () => this.selectionState.props,
      isEditable: (layerId) => this.layers.isEditable(layerId),
      run: (command) => this.run(command),
    });
    this.propAlignment = this.alignment;
    this.selectionState = new PropSelection({
      document: () => this.document,
      map: () => this.map,
      mapId: () => this.mapId,
      get context() {
        return library.context;
      },
      objectTypes: () => this.registeredObjectTypes,
      entityTypes: () => this.registeredEntityTypes,
      isEditable: (layerId) => this.layers.isEditable(layerId),
      isLocked: (layerId) => this.layers.isLocked(layerId),
      isHidden: (layerId) => this.layers.isHidden(layerId),
      requireEditable: (layerId) => this.requireEditable(layerId),
      allocateId: (prefix) => this.allocateId(prefix),
      snap: (x, y) => this.propAuthoring.snapPosition(x, y),
      aligned: (prop) => this.alignment.alignedAt(prop),
      quantise: (value) => this.propAuthoring.quantise(value),
      guardDelete: (things, run) => this.references.guardDelete(things, run),
      deselectStandIn: () => {
        this.viewState.deselectStandIn();
      },
      armed: () => this.brush.drop(),
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.selection = this.selectionState;
    this.brush = new PropBrush({
      document: () => this.document,
      map: () => this.map,
      mapId: () => this.mapId,
      activeLayerId: () => this.layers.activeId,
      isEditable: (layerId) => this.layers.isEditable(layerId),
      lookups: () => ({ frameSize: (frame) => frameSizeOf(library.context, frameId(frame.tileset, null, frame.tile)), footprintOf: (prop) => this.propAuthoring.blockingFootprint(prop), artOf: () => undefined }),
      allocateIds: (prefix, count) => this.allocateIds(prefix, count),
      allocateId: (prefix) => this.allocateId(prefix),
      dropTemplate: () => this.selectionState.setTemplate(null),
      guardDelete: (things, run) => this.references.guardDelete(things, run),
      referencedIds: () => this.references.referencedIds(),
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.sessions = new SessionManager(() => library.context);
    this.sessions.rebuild(this.state.document, this.map);
    this.layerAuthoring = new LayerAuthoring({
      document: () => this.document,
      map: () => this.map,
      mapId: () => this.mapId,
      terrains: () => library.context.terrains,
      offeredTilesets: () => offeredTilesetIds(this.document.tilesets, this.catalogue.tilesets),
      zoneTypeCount: () => this.zoneTypes.length,
      terrainName: (terrainId, colour) => this.terrain.name(terrainId, colour),
      layerTerrainName: (layerId) => this.terrain.layerName(layerId),
      undoDepth: () => this.undoDepth,
      refusals: () => this.live.refusals,
      clearTileSelection: () => this.tileAuthoring.editing.clear(),
      leaveMode: (kind) => {
        this.viewState.deselectStandIn();
        this.selectionState.leaveMode(kind);
      },
      allocateId: (prefix) => this.allocateId(prefix),
      run: (command) => this.run(command),
      notify: () => this.notify(),
      repaintAll: () => this.emitRepaint(null),
    });
    this.layers = this.layerAuthoring;
    this.tileAuthoring = new TileAuthoring({
      document: () => this.document,
      map: () => this.map,
      activeLayerId: () => this.layers.activeId,
      isEditable: (layerId) => this.layers.isEditable(layerId),
      sessionRefuses: () => this.live.refusal() !== null,
      liveRefuses: () => this.liveRefuses(),
      deselectStandIn: () => {
        this.viewState.deselectStandIn();
      },
      run: (command) => this.run(command),
      notify: () => this.notify(),
      emitRepaint: (repaint) => this.emitRepaint(repaint),
    });
    this.tiles = this.tileAuthoring;
    this.fillAuthoring = new FillAuthoring({
      document: () => this.document,
      map: () => this.map,
      activeLayerId: () => this.layers.activeId,
      isEditable: (layerId) => this.layers.isEditable(layerId),
      isLocked: (layerId) => this.layers.isLocked(layerId),
      layerStands: (mapId, layer) => this.layerStands(mapId, layer),
      tileInHand: () => this.tileAuthoring.brush,
      zoneType: () => this.zones.type?.id ?? undefined,
      activeColour: () => this.terrainAuthoring.activeColour(),
      collisionValue: () => this.collision.brush?.value,
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.fill = this.fillAuthoring;
    this.collision = new CollisionAuthoring({
      brushes: () => this.adapter.collisionBrushes ?? [],
      declaredOpen: () => (this.adapter.collision === undefined ? null : (this.adapter.collision(library.context.terrains).open ?? 0)),
      document: () => this.document,
      map: () => this.map,
      get context() {
        return library.context;
      },
      layerCells: (layerId) => this.layerCells(layerId),
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.zones = new ZoneAuthoring({
      zoneTypes: () => this.adapter.zoneTypes ?? NO_ZONE_TYPES,
      objectTypes: () => this.registeredObjectTypes,
      map: () => this.map,
      get context() {
        return library.context;
      },
      tileSize: () => this.document.tileSize,
      activeKind: () => this.map.layers.find((l) => l.id === this.layerAuthoring.activeRaw)?.kind,
      activationOverlay: () => this.activation.overlay,
      areaShown: () => this.area.showsOverlays,
      setActivationOverlay: (shown) => this.activation.setOverlay(shown),
      shape: () => this.collision.shape,
      ellipseStamp: (cx, cy, brushSize) => this.collision.stamp(cx, cy, brushSize),
      isLocked: (layerId) => this.layers.isLocked(layerId),
      isHidden: (layerId) => this.layers.isHidden(layerId),
      allocateId: (prefix) => this.allocateId(prefix),
      idMark: () => this.nextId,
      rewindIds: (mark) => {
        this.nextId = mark;
      },
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    this.doors = new DoorAuthoring({
      document: () => this.document,
      map: () => this.map,
      doorFields: () => this.adapter.doorFields,
      arrivalType: () => this.adapter.arrivalType,
      activeLayer: () => this.map.layers.find((l) => l.id === this.layerAuthoring.activeRaw),
      allocateId: (prefix) => this.allocateId(prefix),
      idMark: () => this.nextId,
      rewindIds: (mark) => {
        this.nextId = mark;
      },
      run: (command) => this.run(command),
      notify: () => this.notify(),
    });
    library.subscribe(() => {
      this.neighbours.clear();
      this.checks.bump();
      this.emitRepaint(null);
      this.notify();
    });
  }

  /**
   * The catalogue and the compile context the store reads through (ADR-130 decision 2.8): art that arrives after
   * the editor opened (`LateFrames`, ADR-129 decision 3.7) grows them, and the store repaints and notifies.
   */
  public readonly library: LibrarySource;

  /**
   * The editor catalogue that stands now.
   *
   * @returns The catalogue; a growth replaces it with a new value.
   */
  public get catalogue(): EditorCatalogue {
    return this.library.catalogue;
  }

  /**
   * The compile context that stands now.
   *
   * @returns The context; a growth replaces it with a new value.
   */
  private get context(): CompileContext {
    return this.library.context;
  }

  /**
   * The zone types the game registers, in registration order (ADR-054's amendment of 2026-09-27).
   *
   * @returns The types, empty when the game registers none.
   */
  public get zoneTypes(): readonly ZoneTypeDefinition[] {
    return this.adapter.zoneTypes ?? NO_ZONE_TYPES;
  }

  /**
   * The collision brushes the game declared (ADR-028).
   *
   * @returns The brushes, possibly none.
   */
  public get collisionBrushes(): readonly CollisionBrush[] {
    return this.adapter.collisionBrushes ?? [];
  }

  /**
   * The surfaces the game lists, in its own words, for a footprint's deck to be given one
   * (`PropFootprint.surface`).
   *
   * @returns The names, none for a game that lists none and for a compile context written before it did.
   */
  public get surfaceNames(): readonly string[] {
    return this.context.surfaces?.names ?? [];
  }

  /**
   * Whether a layer of a map is still the value it was read as: nothing in it has changed since. What a
   * pending fill and a host's waiting rectangle ask, so that an edit elsewhere in the world does not drop
   * them.
   *
   * @param mapId The map.
   * @param layer The layer as it was read.
   * @returns True while the map holds that same layer value.
   */
  public layerStands(mapId: string, layer: LayerDocument): boolean {
    return this.document.maps.find((m) => m.id === mapId)?.layers.find((l) => l.id === layer.id) === layer;
  }

  /**
   * Subscribes to state changes.
   *
   * @param listener Called after every change.
   * @returns Unsubscribe function.
   */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Subscribes to repaints.
   *
   * @param listener Called with the cells to repaint, or null for everything.
   * @returns Unsubscribe function.
   */
  public onRepaint(listener: RepaintListener): () => void {
    this.repaintListeners.add(listener);
    return () => {
      this.repaintListeners.delete(listener);
    };
  }

  /**
   * The current document.
   *
   * @returns The document.
   */
  public get document(): WorldDocument {
    return this.state.document;
  }

  /**
   * The id of the map in view.
   *
   * @returns The id.
   */
  public get mapId(): string {
    return this.openMap;
  }

  /**
   * The map in view.
   *
   * @returns The map document.
   * @throws Error when the map is missing.
   */
  public get map(): MapDocument {
    const map = this.state.document.maps.find((m) => m.id === this.mapId);
    if (map === undefined) {
      throw new Error(`document has no map ${this.mapId}`);
    }
    return map;
  }

  /**
   * Undo depth: with a session, the tab's own edits.
   *
   * @returns Commands that can be undone.
   */
  public get undoDepth(): number {
    return this.live.session?.past.length ?? this.state.history.past.length;
  }

  /**
   * Redo depth: with a session, the tab's own undone edits.
   *
   * @returns Commands that can be redone.
   */
  public get redoDepth(): number {
    return this.live.session?.future.length ?? this.state.history.future.length;
  }

  /**
   * The history as plain data, for the History drawer (ADR-070's amendment "the drawers"). With a session it
   * lists the tab's own edits, one command an edit, an undone one as the edit it was.
   *
   * @returns The done and undone commands.
   */
  public get history(): History {
    return this.live.attached ? this.live.history : this.state.history;
  }

  /**
   * How many commands the history's cap has let go this session; with a session, also the edits a moved
   * tileset dropped.
   *
   * @returns The count.
   */
  public get historyDropped(): number {
    return this.live.attached ? this.live.dropped : this.dropped;
  }

  /**
   * The history's position to record once the document read now has been written (ADR-070 decision 8): a
   * host takes it with the document it serialises, before the write, so an edit made while the save is in
   * flight still reads unsaved when the save is recorded.
   *
   * @returns The position.
   */
  public saveMark(): SavePoint {
    return savePointOf(this.state.history, this.dropped);
  }

  /**
   * Records a written document's position as the last save and tells the subscribers. View state: nothing is
   * saved or undone.
   *
   * @param mark The position `saveMark` gave when the written document was read.
   */
  public markSaved(mark: SavePoint): void {
    this.saved = mark;
    this.notify();
  }

  /**
   * Marks the document as differing from the one last written, with nothing to undo: a host that opened the store
   * on a document it changed as it read it (a game's upgrade of an earlier shape) calls this, so the save state
   * and the prompt before leaving read "Unsaved changes" until a save is recorded. View state: nothing is edited.
   */
  public markUnsaved(): void {
    this.saved = UNSAVED_POINT;
    this.notify();
  }

  /**
   * Whether the document differs from the last one written, read from the history's position and never by
   * comparing documents (ADR-070 decision 8): undoing back to the saved command reads saved again, and
   * undoing past it and editing reads unsaved, since the new command is another. A freshly opened document
   * reads saved. With a session nothing is ever unsaved in that sense: every operation is journaled by the
   * server before it is answered, so this reads true while an operation of the tab's has no answer, which is
   * when leaving the page would lose something (ADR-086 decision 26). That holds for a session that has
   * ended too, stopped until the page is reloaded or without a channel for good: the edit is still drawn and
   * is not known to be with the server, so the reload the host asks for is asked about first, and the
   * builder goes on with it knowingly. An ended session with nothing pending reads saved.
   *
   * @returns True while the save state reads "Unsaved changes", or, with a session, while an operation of
   *   the tab's has no answer.
   */
  public get unsaved(): boolean {
    const session = this.live.session;
    return session === null ? !atSavePoint(this.state.history, this.dropped, this.saved) : session.pending.length > 0;
  }

  /**
   * Whether redo is available.
   *
   * @returns True when a command can be redone.
   */
  public get canRedo(): boolean {
    return this.redoDepth > 0;
  }

  /**
   * The camera the game registers (P1.6-T47), or null when it registers none and the editor has no
   * camera-stop guide to show.
   *
   * @returns The definition.
   */
  public get camera(): CameraDefinition | null {
    return this.adapter.camera ?? null;
  }

  /**
   * The stand-in the game registers (ADR-071 decision 1), or null when it registers none and the editor has
   * no stand-in to show.
   *
   * @returns The definition.
   */
  public get standIn(): StandInDefinition | null {
    return this.adapter.standIn ?? null;
  }

  /**
   * The map's size in world pixels.
   *
   * @returns Width and height.
   */
  private mapPixels(): { width: number; height: number } {
    const tileSize = this.document.tileSize;
    return { width: this.map.width * tileSize, height: this.map.height * tileSize };
  }

  /**
   * What a cell of the map in view holds (`cellStack`): every shown tile and terrain layer that draws it,
   * topmost first, a terrain by the name the palette gives the entry it paints there, read from the cells the
   * map draws.
   *
   * @param cx Cell column.
   * @param cy Cell row.
   * @returns The entries.
   */
  public cellStack(cx: number, cy: number): CellStackEntry[] {
    return cellStack(this.map, cx, cy, {
      nameOf: (terrainId, colour) => this.terrain.name(terrainId, colour),
      tilesets: this.document.tilesets,
      shown: (layerId) => !this.layers.isHidden(layerId),
      cellsOf: (layerId) => this.sessions.get(layerId)?.output ?? null,
      colourOf: (layerId) => this.terrain.layerColour(layerId),
      tilesetName: (tilesetId) => tilesetHeading(tilesetId),
    });
  }

  /**
   * The registered entity types.
   *
   * @returns The definitions.
   */
  public get registeredEntityTypes(): readonly EntityTypeDefinition[] {
    return registeredEntityTypes(this.adapter);
  }

  /**
   * The entity types offered for placing: the registered ones the game has not retired.
   *
   * @returns The definitions, in registration order.
   */
  public get offeredEntityTypes(): readonly EntityTypeDefinition[] {
    return offeredEntityTypes(this.adapter);
  }

  /**
   * The registered object types (ADR-042), which the renderer draws and the compiler stamps.
   *
   * @returns The definitions.
   */
  public get registeredObjectTypes(): readonly ObjectTypeDefinition[] {
    return this.adapter.objectTypes ?? [];
  }

  /**
   * The fields the game gives a map (ADR-052's amendment of 2026-09-26), which the inspector shows with
   * nothing selected.
   *
   * @returns The schema, or null when the game registers none.
   */
  public get mapSchema(): JsonSchema | null {
    return this.adapter.mapSchema ?? null;
  }

  /**
   * Replaces the map's properties as one command (P1.6-T28); a record equal to the map's records nothing. A
   * map field belongs to no layer, so no lock or eye refuses it.
   *
   * @param properties The whole new record.
   */
  public setMapProperties(properties: { readonly [key: string]: JsonValue }): void {
    const command = setMapPropertiesCommand(this.map, properties);
    if (command !== null) {
      this.run(command);
    }
  }

  /**
   * The pointer tool (`view.tool`), on the store itself because the store is the `CellMarkSource` the map's cell
   * mark reads.
   *
   * @returns The tool.
   */
  public get tool(): EditorTool {
    return this.viewState.tool;
  }

  /**
   * The ground cell the select tool last picked (`view.pickedCell`), on the store itself because the store is the
   * `CellMarkSource` the map's cell mark reads.
   *
   * @returns The cell, or null when the last press picked no ground.
   */
  public get pickedCell(): { readonly x: number; readonly y: number } | null {
    return this.viewState.pickedCell;
  }

  /**
   * The footprint the compile context holds for a prop frame (`props.footprintOf`), on the store itself because the
   * store is the `FootprintLibraryView` a footprint write reads.
   *
   * @param frameId Frame id (`<tileset>/default/<tile>`).
   * @returns The footprint, or undefined when the frame has none.
   */
  public footprintOf(frameId: string): PropFootprint | undefined {
    return this.propAuthoring.footprintOf(frameId);
  }

  /**
   * The footprint the build derived for a frame from its art (`props.derivedFootprintOf`), on the store itself
   * because the store is the `FootprintLibraryView` a footprint write reads.
   *
   * @param frameId Frame id.
   * @returns The derived footprint, or undefined when the game's policy gives the frame none.
   */
  public derivedFootprintOf(frameId: string): PropFootprint | undefined {
    return this.propAuthoring.derivedFootprintOf(frameId);
  }

  /**
   * Replaces or removes a prop frame's footprint in the compile context (`props.setFootprint`), on the store
   * itself because the store is the `FootprintLibraryView` a footprint write changes.
   *
   * @param frameId Frame id.
   * @param footprint The footprint, or null to make the frame walkable.
   */
  public setFootprint(frameId: string, footprint: PropFootprint | null): void {
    this.propAuthoring.setFootprint(frameId, footprint);
  }

  /**
   * The depth the library holds for a frame (`props.depthOf`), on the store itself because the store is the
   * `FootprintLibraryView` a footprint write reads and patches.
   *
   * @param frameId Frame id.
   * @returns The depth, or undefined when the frame has none.
   */
  public depthOf(frameId: string): FrameDepth | undefined {
    return this.propAuthoring.depthOf(frameId);
  }

  /**
   * Replaces or removes the depth the editor holds for a frame (`props.setDepth`), on the store itself because
   * the store is the `FootprintLibraryView` a footprint write changes.
   *
   * @param frameId Frame id.
   * @param depth The depth, or null to take it away.
   */
  public setDepth(frameId: string, depth: FrameDepth | null): void {
    this.propAuthoring.setDepth(frameId, depth);
  }

  /**
   * The Behind and In front cells the library holds for a frame (`props.marksOf`), on the store itself because the
   * store is the `FootprintLibraryView` a footprint write reads and patches.
   *
   * @param frameId Frame id.
   * @returns The marks and their cell, or undefined when the frame has none.
   */
  public marksOf(frameId: string): FrameMarks | undefined {
    return this.propAuthoring.marksOf(frameId);
  }

  /**
   * The marks the library holds for a frame apart from a preview (`props.libraryMarksOf`).
   *
   * @param frameId Frame id.
   * @returns The library's marks, or undefined when it holds none.
   */
  public libraryMarksOf(frameId: string): FrameMarks | undefined {
    return this.propAuthoring.libraryMarksOf(frameId);
  }

  /**
   * Shows marks not yet written as the frame's (`props.previewMarks`).
   *
   * @param frameId Frame id.
   * @param marks The marks as painted, or null for none.
   */
  public previewMarks(frameId: string, marks: FrameMarks | null): void {
    this.propAuthoring.previewMarks(frameId, marks);
  }

  /**
   * Ends a preview (`props.dropMarksPreview`).
   *
   * @param frameId Frame id.
   */
  public dropMarksPreview(frameId: string): void {
    this.propAuthoring.dropMarksPreview(frameId);
  }

  /**
   * Replaces or removes the marks the editor holds for a frame (`props.setMarks`), on the store itself because the
   * store is the `FootprintLibraryView` a footprint write changes.
   *
   * @param frameId Frame id.
   * @param marks The marks, or null to take them away.
   */
  public setMarks(frameId: string, marks: FrameMarks | null): void {
    this.propAuthoring.setMarks(frameId, marks);
  }

  /**
   * The revision of what the checks read besides the document (`issues.revision`), on the store itself because the
   * store is the `IssueSource` an `IssueWatch` follows.
   *
   * @returns The revision.
   */
  public get contextRevision(): number {
    return this.checks.revision;
  }

  /**
   * The terrains the context knows.
   *
   * @returns The definitions.
   */
  public get terrains(): readonly TerrainDefinition[] {
    return this.context.terrains;
  }

  /**
   * Lets go of what a layer that is not editable refuses, whichever change locked or hid it (its own lock
   * or eye, its group's, or filing it into a locked or hidden group): the tile selection on such an active
   * layer is dropped, a terrain stroke in progress on one is discarded, and gathered props leave the set
   * when their layer locks or hides or they are gone. Runs before every notification, so no subscriber
   * ever sees a selection or a stroke on a locked or hidden layer.
   */
  private releaseRefused(): void {
    this.fillAuthoring.release();
    const active = this.layers.activeId;
    if (active !== null && !this.layers.isEditable(active)) {
      this.tileAuthoring.editing.drop();
    }
    this.terrainAuthoring.releaseRefused();
    this.activation.release();
    this.references.release();
    this.cellFields.release();
    this.pathFields.release();
    this.selectionState.release();
  }

  /**
   * Refuses work on a locked or hidden layer for callers that must hand back what they made.
   *
   * @param layerId Layer id.
   * @throws Error naming the layer when it is locked or hidden.
   */
  private requireEditable(layerId: string): void {
    this.layerAuthoring.requireEditable(layerId);
  }

  /**
   * The resolved cells of a layer: the incremental session's output for a terrain layer, the document's
   * tiles for a tile layer.
   *
   * @param layerId Layer id.
   * @returns Packed cells, or null for layers without cells.
   */
  public layerCells(layerId: string): Uint32Array | null {
    const preview = this.tileAuthoring.strokes.previewOf(layerId);
    if (preview !== null) {
      return preview;
    }
    const session = this.sessions.get(layerId);
    if (session !== undefined) {
      return session.output;
    }
    const layer = this.map.layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "tile") {
      return null;
    }
    const cells = new Uint32Array(this.map.width * this.map.height).fill(EMPTY_TILE);
    cells.set(layer.tiles);
    return cells;
  }

  /**
   * The drawn cells of a layer of another map of the world, as that map would show them if it were in view
   * (P1.6-T144, ADR-109 decision 25): a terrain layer's tiles resolved by sessions of their own, a tile layer's
   * tiles padded to the map's size. Read only, for the neighbours' edge strips; the arrays are kept while the
   * layer is unchanged, so a strip repainted for a pan reads the same cells.
   *
   * @param mapId The map.
   * @param layerId The layer's id.
   * @returns Packed cells, or null for a layer without cells, a layer or map the world lacks, or a terrain the
   *   context does not know.
   */
  public neighbourCells(mapId: string, layerId: string): Uint32Array | null {
    const map = this.document.maps.find((m) => m.id === mapId);
    const layer = map?.layers.find((l) => l.id === layerId);
    if (map === undefined || layer === undefined || (layer.kind !== "terrain" && layer.kind !== "tile")) {
      return null;
    }
    if (layer.kind === "tile") {
      const held = this.neighbourTiles.get(layer);
      if (held !== undefined && held.length === map.width * map.height) {
        return held;
      }
      const cells = new Uint32Array(map.width * map.height).fill(EMPTY_TILE);
      cells.set(layer.tiles);
      this.neighbourTiles.set(layer, cells);
      return cells;
    }
    let entry = this.neighbours.get(mapId);
    if (entry === undefined || entry.tilesets !== this.document.tilesets) {
      entry = { map, tilesets: this.document.tilesets, sessions: new SessionManager(() => this.context), failed: false, idle: new Set() };
      this.neighbours.set(mapId, entry);
    }
    if (entry.map !== map || (!entry.failed && !entry.sessions.has(layerId) && !entry.idle.has(layerId))) {
      if (entry.map !== map) {
        entry.idle.clear();
      }
      try {
        entry.sessions.rebuild(this.document, map);
        entry.failed = false;
        if (!entry.sessions.has(layerId)) {
          entry.idle.add(layerId);
        }
      } catch {
        entry.failed = true;
      }
      entry.map = map;
    }
    return entry.failed ? null : (entry.sessions.get(layerId)?.output ?? null);
  }

  /**
   * Lets go of the sessions kept for the neighbours' cells, for a guide that is switched off.
   */
  public forgetNeighbours(): void {
    this.neighbours.clear();
  }

  /**
   * Adds a pack to the world (ADR-037 amendment): the pack's tilesets the document lacks, appended as one
   * command, so no packed cell moves.
   *
   * @param packId The pack.
   * @param tilesets The pack's tileset ids, in the order its import yields them.
   * @returns False when the document already lists every one and nothing was recorded.
   * @throws Error when the list is empty or names a tileset of another pack.
   */
  public addPack(packId: string, tilesets: readonly string[]): boolean {
    const command = addPackCommand(this.document, packId, tilesets);
    return command !== null && this.run(command);
  }

  /**
   * Pulls frames of one tileset into the world without its pack (ADR-129 decision 3.4): a partial entry appended,
   * or the entry the world holds widened, as one command, so no packed cell moves. The frames show in the editor
   * after the next compile.
   *
   * @param tileset The tileset's id.
   * @param frames The local tile ids to pull; a strip is pulled by its first frame.
   * @returns False when the world already holds the tileset whole or holds every one of the frames, and nothing
   *   was recorded.
   * @throws Error when the pull would pass the suggested size of an entry or of a world, or a frame is not a
   *   tile id.
   */
  public pullFrames(tileset: string, frames: readonly number[]): boolean {
    const command = pullFramesCommand(this.document, tileset, frames);
    return command !== null && this.run(command);
  }

  /**
   * Pulls frames of several tilesets into the world as one command and one undo (ADR-129 decision 3.9): a prop
   * with the frames its states and transitions rest on, which lands whole or is refused whole.
   *
   * @param picks The tilesets and the local tile ids to pull from each; a strip is pulled by its first frame.
   * @returns False when the world already holds every one of the frames, and nothing was recorded.
   * @throws Error when no frame is named, the pull would pass the suggested size of an entry or of a world, or
   *   a frame is not a tile id. Nothing is recorded then.
   */
  public pullPicks(picks: readonly { readonly tileset: string; readonly frames: readonly number[] }[]): boolean {
    const command = pullPicksCommand(this.document, picks);
    return command !== null && this.run(command);
  }

  /**
   * Takes pulled frames out of the world again (ADR-129 decision 3.10): the partial entry narrowed, or removed
   * when its last frame goes, as one command.
   *
   * @param tileset The tileset's id.
   * @param frames The local tile ids to remove; one the entry does not hold is passed over.
   * @returns False when the world holds the tileset whole, does not list it, or holds none of the frames, and
   *   nothing was recorded.
   * @throws Error naming the first placement when a named frame is still placed, or, when the last frame would
   *   go, naming the first thing that still names the tileset. Nothing is recorded then.
   */
  public dropFrames(tileset: string, frames: readonly number[]): boolean {
    const command = dropFramesCommand(this.document, tileset, frames);
    return command !== null && this.run(command);
  }

  /**
   * Makes the fix an Issues line offers (`IssueRemedy`), as one command: a prop's alignment or its keeping as
   * placed (`PropAlignment.fix`), for `remove-pull` the removal of the pulled frame the line names, or, for `remove-layer`,
   * the removal of the layer the line is about.
   *
   * @param entry The line, with the remedy to make.
   * @returns What the fix did, or null when the line offers no fix. A removal something placed refuses answers
   *   `removed: false` with the refusal's words and records nothing.
   */
  public fixIssue(entry: Pick<IssueEntry, "remedy" | "target" | "frame">): IssueFix | null {
    if (entry.remedy?.id === "remove-layer") {
      if (entry.target.kind !== "layer" || !this.map.layers.some((layer) => layer.id === (entry.target as { layerId: string }).layerId)) {
        return null;
      }
      const before = this.undoDepth;
      this.layers.remove(entry.target.layerId);
      return { remedy: "remove-layer", removed: this.undoDepth > before };
    }
    if (entry.remedy?.id !== "remove-pull") {
      return this.propAlignment.fix(entry);
    }
    if (entry.frame === undefined) {
      return null;
    }
    try {
      return { remedy: "remove-pull", removed: this.dropFrames(entry.frame.tileset, [entry.frame.tile]), refusal: null };
    } catch (error) {
      return { remedy: "remove-pull", removed: false, refusal: error instanceof Error ? error.message : String(error) };
    }
  }

  /**
   * Clears one painted cell of a door layer, as one command: the cell holds no door after, and the door itself
   * stays in the layer's list.
   *
   * @param layerId The door layer.
   * @param index The cell's index in the layer's grid of quarter cells.
   * @returns False when the layer is not a door layer of the map in view, the cell holds no door, or the layer
   *   refuses edits, and nothing was recorded.
   */
  public eraseDoorCell(layerId: string, index: number): boolean {
    const layer = this.map.layers.find((candidate) => candidate.id === layerId);
    const door = layer?.kind === "door" ? doorAt(layer, index) : null;
    return door !== null && this.run({ kind: "paintDoor", mapId: this.map.id, layerId, cells: [{ index, before: door.id, after: null }] });
  }

  /**
   * Removes a pack from the world: every tileset with its prefix, as one command that renumbers every
   * packed cell after them.
   *
   * @param packId The pack.
   * @returns False when the document lists none of its tilesets and nothing was recorded.
   * @throws Error when the world still places anything from the pack, naming the first placement.
   */
  public removePack(packId: string): boolean {
    const command = removePackCommand(this.document, packId);
    return command !== null && this.run(command);
  }

  /**
   * Puts a merged document in place of the tab's (ADR-086 decisions 7 and 8), as another builder's save merged
   * with this tab's work: the history is kept, since a command over what the other builder did not change still
   * undoes exactly; the save point moves to the current position when nothing was unsaved, so the tab reads saved
   * on the new head, and stays when something was. A merge that moves any tileset the tab held to another index in
   * the list empties the undo and redo lists, since removed layers and maps carry tiles packed against the old list
   * and would come back naming other tiles. The map in view stays open, or its neighbour opens when the
   * merge removed it; a pick, a held delete question, Edit on map, a waiting fill and the tile selection are let
   * go, and the selections, hidden and locked layers keep only what the merged document still holds. Nothing
   * enters the history.
   *
   * @param document The merged document.
   * @returns False, adopting nothing, while a stroke is held.
   */
  public adoptMerge(document: WorldDocument): boolean {
    if (this.live.attached) {
      throw new Error("a store with a session adopts no merge: its document is the session's");
    }
    if (this.terrainAuthoring.painting || this.tileAuthoring.strokes.active) {
      return false;
    }
    const wasUnsaved = this.unsaved;
    const before = this.document;
    this.letGoForAdoption();
    this.state = { document: this.terrainAuthoring.tidied(document), history: this.state.history };
    if (!wasUnsaved) {
      this.saved = savePointOf(this.state.history, this.dropped);
    }
    if (before.tilesets.some((tileset, index) => document.tilesets[index]?.id !== tileset.id)) {
      this.dropHistory();
    }
    this.keepWhatStands(before);
    this.sessions.reset();
    this.sessions.rebuild(this.state.document, this.map);
    this.emitRepaint(null);
    this.notify();
    return true;
  }

  /**
   * Ends Edit on map for the cells and path fields.
   */
  private endFieldEdits(): void {
    this.cellFields.endEditing();
    this.pathFields.endEditing();
  }

  /**
   * Lets go of what an adopted document may have changed under the builder's hands: a pick, a held delete
   * question, Edit on map, a waiting fill and the tile selection.
   */
  private letGoForAdoption(): void {
    this.references.cancelPick();
    this.groundPick.end();
    this.references.cancelDelete();
    this.activation.endEditing();
    this.endFieldEdits();
    this.fillAuthoring.drop();
    this.tileAuthoring.editing.clear();
  }

  /**
   * Keeps of the view state only what an adopted document still holds: the map in view stays open, or its
   * neighbour opens when the document no longer holds it; hidden and locked layers, the selections, the active
   * layer and the tile in hand that are gone are let go.
   *
   * @param before The document shown before the adoption.
   */
  private keepWhatStands(before: WorldDocument): void {
    const document = this.state.document;
    if (!document.maps.some((m) => m.id === this.openMap)) {
      const neighbour = neighbourOf(before, before.maps.findIndex((m) => m.id === this.openMap));
      const target = document.maps.some((m) => m.id === neighbour) ? neighbour : (document.maps[0]?.id ?? neighbour);
      this.openMapView(target);
    }
    this.layerAuthoring.mapView.keepPresent();
    this.selectionState.keepPresent();
    this.tileAuthoring.dropMissing();
  }

  /**
   * Shows the session's shown document in place of the one shown (ADR-086 decision 26), with only what the
   * repaint names drawn again: a cell patch repaints its tiles' rectangle on its layer and a terrain patch has
   * its layer's session take its vertices again, never the map; only a change of the map's layers, of a layer's
   * terrain, of the tileset list or of the world's maps draws the map again, as the same command made in this
   * tab does. The selections and the hidden and locked layers keep what the document still holds, as a merge
   * keeps them; a pick, Edit on map, a waiting fill and the tile selection are let go only for a change of
   * structure. Nothing is tidied or added: the document is taken value for value, so every tab's confirmed
   * document is the server's. A store with no session adopts nothing.
   *
   * @param document The session's shown document; any other is refused.
   * @param repaint What differs between the document shown and it, as `liveRepaintOf` names it.
   * @returns False, adopting nothing, while a stroke is held, for a document that is not the session's shown
   *   one, and for a store with no session.
   */
  public adoptLive(document: WorldDocument, repaint: LiveRepaint): boolean {
    if (document !== this.live.session?.shown || this.terrainAuthoring.painting || this.tileAuthoring.strokes.active) {
      return false;
    }
    const before = this.state.document;
    if (document === before) {
      this.notify();
      return true;
    }
    this.state = { document, history: EMPTY_HISTORY };
    this.redrawLive(before, repaint);
    this.notify();
    return true;
  }

  /**
   * Draws again what a repaint of the session names, on the map in view, the document already in place.
   *
   * @param before The document shown before.
   * @param repaint What differs.
   */
  private redrawLive(before: WorldDocument, repaint: LiveRepaint): void {
    const redraw = liveRedrawOf(before, this.state.document, repaint, this.openMap);
    if (redraw.structure) {
      this.letGoForAdoption();
      this.keepWhatStands(before);
    }
    redraw.renamed.forEach((layerId) => this.terrainAuthoring.recoverColour(layerId));
    if (redraw.all) {
      if (redraw.reset) {
        this.sessions.reset();
      }
      this.sessions.rebuild(this.state.document, this.map);
      this.emitRepaint(null);
      return;
    }
    for (const layerId of redraw.faces) {
      const rect = this.sessions.applyFaceHeight(this.map, layerId);
      this.emitRepaint(rect === null ? null : { layerId, rect });
    }
    for (const { layerId, indices } of redraw.terrain) {
      const rect = this.sessions.resync(this.map, layerId, indices);
      this.emitRepaint(rect === null ? null : { layerId, rect });
    }
    redraw.rects.forEach((part) => this.emitRepaint(part));
  }

  /**
   * Whether the session takes no command now, telling the host why: what a tool asks before it starts a
   * stroke, so a paused or view-only tab paints nothing it would have to take back.
   *
   * @returns True when a session refuses; false when it takes commands or the store has none.
   */
  private liveRefuses(): boolean {
    const refusal = this.live.refusal();
    if (refusal !== null) {
      this.live.refuse(refusal);
    }
    return refusal !== null;
  }

  /**
   * Gives the ids this tab mints a tag of their own (ADR-086 decision 9), so a tab beside others on one world
   * never mints an id another tab mints: `<prefix>-<tag>-<n>` instead of `<prefix>-<n>`, a new map's id
   * included (`map-<tag>-<n>`).
   *
   * @param tag One to eight lowercase letters and digits.
   * @throws Error when the tag is not one.
   */
  public setIdTag(tag: string): void {
    if (!/^[a-z0-9]{1,8}$/.test(tag)) {
      throw new Error(`an id tag is 1 to 8 lowercase letters and digits, not ${tag}`);
    }
    this.idTag = tag;
  }

  /**
   * Empties the undo and redo lists, keeping the save state as it read: saved stays saved at the empty list,
   * unsaved stays unsaved.
   */
  private dropHistory(): void {
    const unsaved = this.unsaved;
    this.state = { document: this.state.document, history: EMPTY_HISTORY };
    this.saved = unsaved ? UNSAVED_POINT : savePointOf(EMPTY_HISTORY, this.dropped);
    this.notify();
  }

  /**
   * Undoes the last command. A command whose recorded values no longer hold (another builder's save, merged in,
   * changed what it changed; ADR-086 decision 8) is not undone: the undo and redo lists are emptied instead.
   * With a session the undo is the session's (decision 27): the tab's own newest edit that still can be taken
   * back, key by key where nobody changed it since, sent as an operation; the host is told what became of it.
   *
   * @returns True when something was undone.
   */
  public undo(): boolean {
    if (this.live.attached) {
      return this.turnLive(false);
    }
    if (this.terrainAuthoring.painting || this.tileAuthoring.strokes.active || !canUndo(this.state.history)) {
      return false;
    }
    const command = this.state.history.past[this.state.history.past.length - 1] as Command;
    this.openMapOf(command, "removeMap");
    try {
      this.state = undo(this.state);
    } catch {
      this.dropHistory();
      return false;
    }
    this.areaEdits.turned(command, false);
    this.tileAuthoring.editing.clear();
    this.afterStructuralChange(command);
    this.notify();
    return true;
  }

  /**
   * Redoes the last undone command; one that no longer applies empties the lists, as `undo` does. With a
   * session the redo is the session's, by the same rule as its undo.
   *
   * @returns True when something was redone.
   */
  public redo(): boolean {
    if (this.live.attached) {
      return this.turnLive(true);
    }
    if (this.terrainAuthoring.painting || this.tileAuthoring.strokes.active || !canRedo(this.state.history)) {
      return false;
    }
    const command = this.state.history.future[this.state.history.future.length - 1] as Command;
    this.openMapOf(command, "addMap");
    try {
      this.state = redo(this.state);
    } catch {
      this.dropHistory();
      return false;
    }
    this.areaEdits.turned(command, true);
    this.tileAuthoring.editing.clear();
    this.afterStructuralChange(command);
    this.notify();
    return true;
  }

  /**
   * The document as it stands without the trial of a first stroke (ADR-129 A4): what a writer (a save, a download, a
   * copy, the update's carry) serialises, so the tilesets and the layer a stroke still held would add are never
   * written before the stroke is recorded. The same as `document` when no trial stands.
   *
   * @returns The document.
   */
  public get committedDocument(): WorldDocument {
    return this.trialBase ?? this.state.document;
  }

  /**
   * Runs edits as a trial: what `make` does to the document is shown and worked on, but kept out of the history and
   * the session, and handed back as the commands it was made of. The caller takes the trial back with `restore` and
   * records the commands again, with the work done in the meantime, as one command (a first stroke that brings
   * the art it paints with in the same step, ADR-129 A4). Nothing travels to a session while a trial stands.
   *
   * @param make Makes the edits, through the store's own methods.
   * @returns The commands the trial was made of and how to take it back, or null when a trial already stands.
   * @throws Error when an edit of the trial does not apply; the document is as it was then.
   */
  public provisional(make: () => void): { readonly commands: readonly Command[]; readonly restore: () => void } | null {
    if (this.captured !== null || this.trialBase !== null) {
      return null;
    }
    const before = this.state;
    const activeBefore = this.layers.activeId;
    this.captured = [];
    try {
      make();
    } catch (error) {
      this.captured = null;
      this.state = before;
      this.sessions.rebuild(before.document, this.map);
      if (activeBefore !== null && this.map.layers.some((layer) => layer.id === activeBefore)) {
        this.layers.setActive(activeBefore);
      }
      throw error;
    }
    const commands = this.captured;
    this.captured = null;
    this.trialBase = before.document;
    return {
      commands,
      restore: () => {
        this.trialBase = null;
        const shown = this.live.session?.shown;
        this.state = shown === undefined ? before : { document: shown, history: EMPTY_HISTORY };
        this.sessions.rebuild(this.state.document, this.map);
        if (activeBefore !== null && this.map.layers.some((layer) => layer.id === activeBefore)) {
          this.layers.setActive(activeBefore);
        }
        this.emitRepaint(null);
        this.notify();
      },
    };
  }

  /**
   * Executes a command and keeps sessions in line with the document. Every edit passes here, finished
   * terrain strokes included, so this is where a lock and a hidden layer hold: a command that edits a
   * locked layer or changes a hidden layer's content (P1.6-T9), or a compound any of whose commands does,
   * is dropped unrecorded.
   *
   * With a session (ADR-086 decision 26, ruling b of the amendment of the bounds) the command is first turned
   * into operations and resolved on the shown document: one that cannot travel, that a limit would skip or
   * that the session does not take (a paused, view-only or frozen tab) is refused, nothing changed and the
   * host told why; a tab the session refuses outright pays nothing for the command. Otherwise the session takes the operations, which are the ones sent, and the store shows
   * the session's shown document. The store's own result of the command is compared with it cell by cell and
   * item by item (a zone cell by its type; a new map's unpinned Players band, which a load pins, is not a
   * difference): they agree by meaning, and where they ever did not, the session's is the one shown, what
   * differs is drawn again and the host is told.
   *
   * @param command The command.
   * @returns False when a lock, a hidden layer or the session refused the command.
   * @throws Error when the command does not apply to the document.
   */
  private run(command: Command): boolean {
    if (layersEditedBy(command).some((layerId) => this.layers.isLocked(layerId)) || layersContentEditedBy(command).some((layerId) => this.layers.isHidden(layerId))) {
      return false;
    }
    if (this.captured !== null) {
      this.state = { document: applyCommand(this.state.document, command), history: this.state.history };
      this.captured.push(command);
      this.afterStructuralChange(command);
      this.notify();
      return true;
    }
    if (this.live.attached) {
      if (this.liveRefuses()) {
        return false;
      }
      const before = this.state.document;
      const own = applyCommand(before, command);
      const shown = this.live.take(command);
      if (shown === null) {
        return false;
      }
      this.state = { document: shown, history: EMPTY_HISTORY };
      this.afterStructuralChange(command);
      const drift = liveRepaintOf(own, shown).filter((part) => part.kind === "cells" || part.kind === "items");
      if (drift.length > 0) {
        this.redrawLive(own, drift);
        this.live.mismatch();
      }
      this.notify();
      this.live.settle();
      return true;
    }
    this.dropped += droppedByExecute(this.state.history);
    this.state = execute(this.state, command);
    this.afterStructuralChange(command);
    this.notify();
    return true;
  }

  /**
   * Undoes or redoes through the session: the map the edit was made on is opened first, as a local undo opens
   * it, and what the session then shows is adopted with what changed drawn again.
   *
   * @param redo Whether to redo.
   * @returns True when something was put back.
   */
  private turnLive(redo: boolean): boolean {
    if (this.terrainAuthoring.painting || this.tileAuthoring.strokes.active) {
      return false;
    }
    const step = this.live.turn(redo);
    if (step === null) {
      return false;
    }
    const target = step.repaint.flatMap((part) => (part.kind === "world" ? [] : [part.map]))[0];
    if (target !== undefined && target !== this.openMap && this.document.maps.some((m) => m.id === target) && step.session.shown.maps.some((m) => m.id === target)) {
      this.openMapView(target);
    }
    this.tileAuthoring.editing.clear();
    this.adoptLive(step.session.shown, step.repaint);
    if (this.areaEdits.turnedLive(redo)) {
      this.notify();
    }
    this.live.settle();
    return true;
  }

  /**
   * Re-syncs sessions after a command was applied, undone or redone. A compound that changes a layer's
   * terrain (a replace, ADR-097) is resynced by that change's rebuild and whole repaint alone: its corner
   * fills are skipped, since a session of the layer's other terrain cannot take the corners the layer now
   * holds.
   *
   * @param command The command.
   */
  private afterStructuralChange(command: Command): void {
    switch (command.kind) {
      case "compound": {
        const retargets = command.commands.some((part) => part.kind === "setLayerTerrain");
        command.commands.forEach((part) => {
          if (!retargets || part.kind !== "fillCells") {
            this.afterStructuralChange(part);
          }
        });
        return;
      }
      case "paintCorners": {
        const rect = this.sessions.resync(this.map, command.layerId, command.cells.map((c) => c.index));
        this.emitRepaint(rect === null ? null : { layerId: command.layerId, rect });
        return;
      }
      case "paintCollision":
        this.emitRepaint({ layerId: command.layerId, rect: cellBounds(command.cells.map((c) => c.index), this.map.width) });
        return;
      case "fillCells": {
        const kind = this.map.layers.find((l) => l.id === command.layerId)?.kind;
        if (kind === "zone") {
          return;
        }
        const rect = kind === "terrain" ? this.sessions.resync(this.map, command.layerId, command.indices) : cellBounds(command.indices, kind === "collision" ? this.map.width * COLLISION_CELLS_PER_TILE : this.map.width);
        this.emitRepaint(rect === null ? null : { layerId: command.layerId, rect: kind === "collision" ? tileBoundsOfCollision(rect) : rect });
        return;
      }
      case "renameLayer":
        if (command.mapId === this.mapId) {
          this.terrainAuthoring.recoverColour(command.layerId);
        }
        return;
      case "setLayerTerrain":
        if (command.mapId === this.mapId) {
          this.terrainAuthoring.recoverColour(command.layerId);
          this.terrainAuthoring.keepPaintedColour(command.layerId);
        }
        this.sessions.rebuild(this.state.document, this.map);
        this.emitRepaint(null);
        return;
      case "setLayerFaceHeight": {
        const rect = this.sessions.applyFaceHeight(this.map, command.layerId);
        this.emitRepaint(rect === null ? null : { layerId: command.layerId, rect });
        return;
      }
      case "addLayer":
        if (command.mapId === this.mapId) {
          this.terrainAuthoring.keepPaintedColour(command.layer.id);
        }
        this.sessions.rebuild(this.state.document, this.map);
        this.emitRepaint(null);
        return;
      case "removeLayer":
      case "moveLayer":
        this.sessions.rebuild(this.state.document, this.map);
        this.emitRepaint(null);
        return;
      case "setTiles":
        this.emitRepaint({ layerId: command.layerId, rect: cellBounds(command.cells.map((c) => c.index), this.map.width) });
        return;
      case "changeTilesets":
        this.sessions.reset();
        this.sessions.rebuild(this.state.document, this.map);
        this.tileAuthoring.dropMissing();
        this.emitRepaint(null);
        return;
      default:
        return;
    }
  }

  /**
   * Opens the map an undo or a redo of a command must be seen on, before it is applied (ADR-021's amendment
   * "several maps", decision 3): the map the command edits, or, when the command takes the map in view out
   * of the world (undoing its addition, redoing its removal, a removal with its seams included), the map
   * before it.
   *
   * @param command The command about to be undone or redone.
   * @param keeps The map command that, applied this way, keeps its map in the world: `removeMap` for an undo,
   *   `addMap` for a redo.
   */
  private openMapOf(command: Command, keeps: "addMap" | "removeMap"): void {
    const target = commandMap(command);
    if (target !== null) {
      this.openMapView(target);
      return;
    }
    const part = command.kind === "compound" ? command.commands.find((c) => c.kind === "addMap" || c.kind === "removeMap") : command;
    if (part !== undefined && (part.kind === "addMap" || part.kind === "removeMap") && part.kind !== keeps && part.map.id === this.mapId) {
      const leaving = part.map.id;
      this.openMapView(neighbourOf(this.document, this.document.maps.findIndex((m) => m.id === leaving)));
    }
  }

  /**
   * Puts another map in view (ADR-070's amendment "several maps", decision 6): keeps the map left's active
   * layer and hidden and locked layers and groups for the session and puts back the ones kept for the map
   * opened, lets go of what belongs to the map left (the selections, the tile selection, a pick, a held
   * delete question, Edit on map, a waiting fill, the stand-in's pick, the cell Select picked), rebuilds the terrain sessions and
   * announces a whole repaint and a change. View state: nothing enters history.
   *
   * @param mapId The map.
   * @returns False when it is already in view or a stroke is held.
   * @throws Error when the world has no such map.
   */
  private openMapView(mapId: string): boolean {
    if (mapId === this.openMap || this.terrainAuthoring.painting) {
      return false;
    }
    const map = this.document.maps.find((m) => m.id === mapId);
    if (map === undefined) {
      throw new Error(`the world has no map ${mapId}`);
    }
    this.references.cancelPick();
    this.groundPick.end();
    this.references.cancelDelete();
    this.activation.endEditing();
    this.endFieldEdits();
    this.mapViews.set(this.openMap, this.layerAuthoring.mapView.view());
    this.openMap = mapId;
    this.neighbours.clear();
    this.viewState.clearViewCentre();
    this.layerAuthoring.mapView.restore(this.mapViews.get(mapId), map);
    this.selectionState.clear();
    this.area.clear();
    this.fillAuthoring.drop();
    this.viewState.forgetPicks();
    this.tileAuthoring.editing.clear();
    this.sessions.rebuild(this.document, map);
    this.emitRepaint(null);
    this.notify();
    return true;
  }

  /**
   * Allocates an id unused by any layer, group, prop or entity of any map of the world (ADR-021's amendment
   * "several maps", decision 4).
   *
   * @param prefix Id prefix.
   * @returns The id.
   */
  private allocateId(prefix: string): string {
    return this.allocateIds(prefix, 1)[0] as string;
  }

  /**
   * Allocates ids unused by any layer, group, prop or entity of any map of the world, each once, gathering the
   * world's ids in one pass however many are asked for (ADR-123 decision 6.4). The counter starts at 1 for every
   * store, so the check is what keeps an id the opened world already holds (a saved `group-1`) from being handed
   * out again; on the hosted editor ids read `<prefix>-<tag>-<n>`.
   *
   * @param prefix Id prefix.
   * @param count How many.
   * @returns The ids, in the order handed out.
   */
  public allocateIds(prefix: string, count: number): string[] {
    const ids: string[] = [];
    if (count <= 0) {
      return ids;
    }
    const used = new Set<string>();
    for (const map of this.document.maps) {
      (map.groups ?? []).forEach((g) => used.add(g.id));
      for (const layer of map.layers) {
        used.add(layer.id);
        if (layer.kind === "prop") {
          layer.props.forEach((p) => used.add(p.id));
        } else if (layer.kind === "entity") {
          layer.entities.forEach((e) => used.add(e.id));
        } else if (layer.kind === "door") {
          layer.doors.forEach((d) => used.add(d.id));
        }
      }
    }
    while (ids.length < count) {
      const id = this.idTag === null ? `${prefix}-${this.nextId}` : `${prefix}-${this.idTag}-${this.nextId}`;
      this.nextId += 1;
      if (!used.has(id)) {
        ids.push(id);
      }
    }
    return ids;
  }

  /**
   * Notifies state listeners.
   */
  private notify(): void {
    this.areaEdits.invalidate();
    this.tileAuthoring.strokes.dropIfStale();
    this.releaseRefused();
    const { width, height } = this.mapPixels();
    this.standInFeet.clampInto(width, height);
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * Notifies repaint listeners.
   *
   * @param repaint Cells to repaint, or null for everything.
   */
  private emitRepaint(repaint: Repaint | null): void {
    for (const listener of this.repaintListeners) {
      listener(repaint);
    }
  }
}
